import * as React from "react";
import {
  measureVial,
  getReferenceBox,
  computeVialTransform,
  type VialTransform,
} from "@/lib/vialMeasure";

export interface VialImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

/**
 * Drop-in replacement for product-card vial <img> elements.
 * Measures the image's visible vial bounding box against the reference vial
 * (Tirzepatide) and applies uniform scaling and translation so all vials
 * line up at the same visible height and position.
 *
 * If measurement fails (e.g. CORS or opaque photo), gracefully renders
 * the plain image without transform.
 */
export function VialImage({
  src,
  alt,
  className = "",
  style,
  ...props
}: VialImageProps) {
  const [transform, setTransform] = React.useState<VialTransform | null>(null);

  React.useEffect(() => {
    let active = true;

    if (!src) {
      setTransform(null);
      return;
    }

    Promise.all([measureVial(src), getReferenceBox()])
      .then(([box, ref]) => {
        if (!active) return;
        if (box && ref) {
          const t = computeVialTransform(box, ref, src);
          setTransform(t);
        } else {
          setTransform(null);
        }
      })
      .catch(() => {
        if (active) setTransform(null);
      });

    return () => {
      active = false;
    };
  }, [src]);

  // Transform styles applied when both target and reference measurements succeed
  const transformStyle: React.CSSProperties = transform
    ? {
        transform: `translate(${transform.tx.toFixed(2)}%, ${transform.ty.toFixed(2)}%) scale(${transform.scale.toFixed(4)})`,
        transformOrigin: `${transform.originX.toFixed(2)}% ${transform.originY.toFixed(2)}%`,
        ...style,
      }
    : (style || {});

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={transformStyle}
      {...props}
    />
  );
}

export default VialImage;
